import { useState } from 'react';
import { Award, FileText, PencilIcon, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

import {
  ProfileCard,
  ProfileEmptySection,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { PriorCertification } from '@sandtech-hub-monorepo/ehub-client';

import { PriorCertificationEditDialog } from './PriorCertificationEditDialog';

interface Props {
  certifications?: PriorCertification[];
}

/** Prior Certifications section of the profile: self-reported certifications earned before joining. */
export const PriorCertificationsSection = ({ certifications = [] }: Props) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedCertification, setEditedCertification] = useState<
    PriorCertification | undefined
  >();

  const openEditDialog = (certification?: PriorCertification) => {
    setEditedCertification(certification);
    setEditDialogOpen(true);
  };

  return (
    <>
      <ProfileCard
        title="Prior Certifications"
        editable
        plusInsteadOfPencil
        action={() => openEditDialog(undefined)}
      >
        {certifications.length ? (
          <div className="flex flex-col">
            {certifications.map((cert) => (
              <div key={cert.id} className="flex items-start gap-4 py-3">
                <div className="flex items-center justify-center size-[38px] shrink-0 rounded-[14px] border border-border text-primary">
                  <Award className="h-5 w-5" />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-base text-muted-foreground">
                    {cert.institute}
                  </span>
                  {cert.verificationUrl ? (
                    <Link
                      to={cert.verificationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-medium text-foreground hover:underline"
                    >
                      {cert.courseName}
                    </Link>
                  ) : (
                    <span className="text-lg font-medium text-foreground">
                      {cert.courseName}
                    </span>
                  )}
                </div>
                <span className="text-lg font-medium text-foreground text-right shrink-0">
                  {format(new Date(cert.completedAt), 'MMM yyyy')}
                </span>
                <div
                  onClick={() => openEditDialog(cert)}
                  className="h-10 w-10 p-2 rounded-full hover:bg-muted cursor-pointer shrink-0"
                >
                  <PencilIcon className="text-popover-foreground" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ProfileEmptySection
            header="Add your certifications!"
            main="Include any industry certifications or completed training programs"
            callToActionText="Add Cert"
            callToAction={() => openEditDialog(undefined)}
            icon={<FileText className="h-4 w-4 text-primary" />}
            headerClassName="text-heading"
            headerSizeClassName="text-sm mt-2 mb-1"
            iconWrapperClassName="w-12 h-12 flex items-center justify-center rounded-full border-[1.5px] border-border"
            ctaIcon={<Plus className="h-4 w-4" />}
            buttonClassName="bg-transparent hover:bg-primary/10 hover:text-primary"
          />
        )}
      </ProfileCard>

      <PriorCertificationEditDialog
        open={editDialogOpen}
        handleOpenChange={setEditDialogOpen}
        certification={editedCertification}
      />
    </>
  );
};
